import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { youTubeEmbed } from '../../data/site';

/*
  Shared lightbox for every YouTube film on the landing page. One implementation
  so the hero overview and the customer stories behave identically: escape to
  close, click the backdrop to close, focus moved into the dialog and restored
  to the trigger on close, and the page behind kept from scrolling.

  The iframe is only mounted while the modal is open, so no video player is
  downloaded on first paint.
*/
const VideoModal = ({ video, onClose }) => {
  const closeRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!video) return undefined;

    previouslyFocused.current = document.activeElement;
    closeRef.current?.focus();

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused.current?.focus?.();
    };
  }, [video, onClose]);

  if (!video) return null;

  return (
    <div
      className="ab-modal"
      role="dialog"
      aria-modal="true"
      aria-label={video.headline || 'AtadaBoard video'}
      onClick={onClose}
    >
      <div className="ab-modal__panel" onClick={(event) => event.stopPropagation()}>
        <div className="ab-modal__head">
          <div className="ab-modal__label">
            <span className="ab-modal__role">{video.role}</span>
            <h2 className="ab-modal__title">{video.headline}</h2>
          </div>
          <button
            type="button"
            ref={closeRef}
            className="ab-modal__close"
            onClick={onClose}
            aria-label="Close video"
          >
            <X size={20} strokeWidth={1.75} />
          </button>
        </div>

        <div className="ab-modal__player">
          <iframe
            src={youTubeEmbed(video.videoId)}
            title={video.headline}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div>

      <style jsx>{`
        .ab-modal {
          position: fixed;
          inset: 0;
          z-index: var(--ab-z-modal);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: var(--ab-gutter);
          background: rgba(6, 41, 68, 0.72);
          backdrop-filter: blur(6px);
          animation: abModalIn 0.24s var(--ab-ease);
        }

        @keyframes abModalIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .ab-modal__panel {
          width: 100%;
          max-width: 960px;
          background: var(--ab-paper);
          border-radius: var(--ab-r-card);
          overflow: hidden;
          box-shadow: var(--ab-shadow-lg);
        }

        .ab-modal__head {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 1rem;
          padding: 1.125rem 1.25rem;
        }

        .ab-modal__role {
          display: block;
          font-size: 0.8125rem;
          font-weight: 600;
          color: var(--ab-blue);
          margin-bottom: 0.25rem;
        }

        .ab-modal__title {
          font-size: 1.0625rem;
          line-height: 1.3;
        }

        .ab-modal__close {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 38px;
          height: 38px;
          border-radius: var(--ab-r-pill);
          border: 1px solid var(--ab-line);
          background: var(--ab-paper);
          color: var(--ab-ink-2);
          cursor: pointer;
          transition: background-color 0.2s var(--ab-ease), color 0.2s var(--ab-ease);
        }

        .ab-modal__close:hover {
          background: var(--ab-blue-50);
          color: var(--ab-blue);
        }

        .ab-modal__close:focus-visible {
          outline: 2px solid var(--ab-blue);
          outline-offset: 2px;
        }

        .ab-modal__player {
          position: relative;
          aspect-ratio: 16 / 9;
          background: var(--ab-navy);
        }

        .ab-modal__player iframe {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          border: 0;
        }
      `}</style>
    </div>
  );
};

export default VideoModal;
